import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarPlus, ChartBar, ChatCircle, Gift, House, Lightning, PencilSimple, QrCode, Scan, SignOut, UserPlus, UsersThree, Wallet, ArrowLeft, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, fmtErr, mediaUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { startCheckout } from "../guest/Profile";
import { formatPlanPrice, intervalLabel, yearlySavings, BillingToggle, usePricing } from "../../hooks/usePricing";
import WalletPanel from "../../components/WalletPanel";
import MediaUpload from "../../components/MediaUpload";
import MessagesPanel from "../../components/MessagesPanel";
import QrScanner from "../../components/QrScanner";
import FounderCityDialog from "../../components/FounderCityDialog";
import AddressAutocomplete from "../../components/AddressAutocomplete";

const NAV = [
  { id: "overview", label: "Overview", icon: House },
  { id: "analytics", label: "Analytics", icon: ChartBar },
  { id: "scanner", label: "Scanner", icon: Scan },
  { id: "events", label: "Events", icon: CalendarPlus },
  { id: "drops", label: "NITE Drops", icon: Lightning },
  { id: "promoters", label: "Promoters", icon: UsersThree },
  { id: "staff", label: "Staff", icon: UserPlus },
  { id: "messages", label: "Messages", icon: ChatCircle },
  { id: "edit", label: "Edit Venue", icon: PencilSimple },
  { id: "rewards", label: "Rewards", icon: Gift },
  { id: "wallet", label: "Wallet", icon: Wallet },
];

function Stat({ label, value, accent = "text-white" }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5">
      <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className={`mt-2 font-display text-3xl font-black ${accent}`}>{value}</div>
    </div>
  );
}

export default function VenueDashboard() {
  const { user, logout, refreshUser } = useAuth();
  const pricing = usePricing();
  const [billing, setBilling] = useState("yearly");
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "overview");
  const [data, setData] = useState(null);
  const [scans, setScans] = useState([]);
  const [drops, setDrops] = useState([]);
  const [scanForm, setScanForm] = useState({ token: "", entry_point: "MAIN ENTRANCE", amount_paid: 0 });
  const [scanResult, setScanResult] = useState(null);
  const [scanBusy, setScanBusy] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [dropForm, setDropForm] = useState({ title: "NITE DROP", perk: "", total: 25, minutes: 60 });
  const [inviteEmail, setInviteEmail] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [staffEmail, setStaffEmail] = useState("");
  const [milestonesInput, setMilestonesInput] = useState("");
  const [editForm, setEditForm] = useState({ name: "", address: "", city: "", open_until: "", cover: "" });
  const [myVenues, setMyVenues] = useState([]);
  const [addBilling, setAddBilling] = useState("monthly");
  const [cityAsk, setCityAsk] = useState(null);
  const askCity = () => new Promise((resolve) => setCityAsk(() => resolve));
  const [npRewards, setNpRewards] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    if (tab === "analytics") {
      API.get("/venue/analytics?days=30").then((r) => setAnalytics(r.data)).catch((e) => toast.error(fmtErr(e)));
    }
  }, [tab, data?.venue?.id]);

  const toggleNpReward = async (r) => {
    try {
      await API.post("/venue/reward-acceptance", { reward_id: r.id, accepted: !r.accepted });
      setNpRewards((list) => list.map((x) => (x.id === r.id ? { ...x, accepted: !x.accepted } : x)));
      toast.success(r.accepted ? `"${r.title}" no longer accepted` : `"${r.title}" accepted at your door`);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const [promoterResults, setPromoterResults] = useState([]);
  const [showEvent, setShowEvent] = useState(false);
  const [eventForm, setEventForm] = useState({ title: "", date_label: "Friday", start: "10 PM", end: "2 AM", address: "", specials: "", cover: 20, list_perk: "Free before 11 PM", tonight: true, promoter_email: "", image_path: null, media: [] });
  const navigate = useNavigate();
  const tokenRef = useRef(null);

  const load = () => {
    API.get("/venue/overview").then((r) => setData(r.data)).catch((e) => toast.error(fmtErr(e)));
    API.get("/venue/scans").then((r) => setScans(r.data)).catch(() => {});
    API.get("/venue/drops").then((r) => setDrops(r.data)).catch(() => {});
    API.get("/venue/staff").then((r) => setStaffList(r.data)).catch(() => {});
    API.get("/venue/list").then((r) => setMyVenues(r.data)).catch(() => {});
  };

  const switchVenue = async (venueId) => {
    try {
      await API.post("/venue/switch", { venue_id: venueId });
      await refreshUser();
      load();
      toast.success("Venue switched");
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(load, []);

  useEffect(() => {
    if (tab === "scanner") setTimeout(() => tokenRef.current?.focus(), 100);
  }, [tab]);

  useEffect(() => {
    setMilestonesInput((data?.venue?.door_milestones || []).join(", "));
  }, [data?.venue?.id]);

  useEffect(() => {
    const v = data?.venue;
    if (v) {
      setEditForm({
        name: v.name || "", address: v.address || "", city: v.city || "",
        open_until: v.open_until || "", cover: v.cover ?? "", geo: null,
      });
    }
  }, [data?.venue?.id]);

  useEffect(() => {
    if (tab === "rewards" && data?.venue?.id) {
      API.get("/venue/reward-acceptance").then((r) => setNpRewards(r.data)).catch(() => setNpRewards([]));
    }
  }, [tab, data?.venue?.id]);

  useEffect(() => {
    if (tab !== "promoters" || inviteEmail.trim().length < 2) {
      setPromoterResults([]);
      return;
    }
    const t = setTimeout(() => {
      API.get(`/venue/promoters/search?q=${encodeURIComponent(inviteEmail)}`)
        .then((r) => setPromoterResults(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [inviteEmail, tab]);

  const doScan = async (tokenOverride = null) => {
    const token = String(tokenOverride || scanForm.token).trim();
    if (!token) return;
    setScanBusy(true);
    setScanResult(null);
    try {
      const { data } = await API.post("/business/scan", {
        token, entry_point: scanForm.entry_point,
        amount_paid: Number(scanForm.amount_paid) || 0,
      });
      setScanResult(data);
      setScanForm({ ...scanForm, token: "" });
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setScanBusy(false);
    }
  };

  const createDrop = async (e) => {
    e.preventDefault();
    try {
      await API.post("/venue/drops", { ...dropForm, total: Number(dropForm.total), minutes: Number(dropForm.minutes) });
      toast.success("NITE DROP live — favorited members notified");
      setDropForm({ title: "NITE DROP", perk: "", total: 25, minutes: 60 });
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const invite = async (query = inviteEmail) => {
    try {
      const { data } = await API.post("/venue/promoters/invite", { email: query });
      toast.success(`${data.handle} added to your promoter team`);
      setInviteEmail("");
      setPromoterResults([]);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const createEvent = async (e) => {
    e.preventDefault();
    try {
      await API.post("/venue/events", {
        ...eventForm,
        address: eventForm.address || venue?.address || "",
        cover: Number(eventForm.cover),
        promoter_email: eventForm.promoter_email || null,
      });
      toast.success("Event published");
      setShowEvent(false);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const addVenueImage = async (m) => {
    if (!m) return;
    try {
      await API.post("/venue/images", { image_url: m.url });
      toast.success("Venue photo added");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const removeVenueImage = async (url) => {
    try {
      await API.post("/venue/images/remove", { image_url: url });
      toast.success("Photo removed");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const setCardBackground = async (m) => {
    if (!m) return;
    try {
      await API.post("/venue/card-background", { image_url: m.url });
      toast.success("Custom card background set");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const clearCardBackground = async () => {
    try {
      await API.post("/venue/card-background", { image_url: "" });
      toast.success("Custom background removed — using venue photo");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const addStaff = async () => {
    try {
      const { data } = await API.post("/venue/staff", { email: staffEmail });
      toast.success(`${data.handle} can now scan at your door`);
      setStaffEmail("");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const removeStaff = async (id) => {
    try {
      await API.delete(`/venue/staff/${id}`);
      toast.success("Staff access removed");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const VENUE_RECAP_KEYS = [["guests", "Guests"], ["revenue", "Revenue"], ["rewards", "Rewards"], ["top_promoter", "Top promoter"]];

  const toggleVenueRecap = async (key) => {
    const current = { guests: true, revenue: true, rewards: true, top_promoter: true, ...(data?.venue?.recap_prefs || {}) };
    try {
      await API.post("/venue/recap-prefs", { prefs: { [key]: !current[key] } });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const saveDoorMilestones = async () => {
    const milestones = milestonesInput.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => Number.isFinite(n) && n > 0);
    try {
      const { data: res } = await API.post("/venue/door-milestones", { milestones });
      setMilestonesInput(res.door_milestones.join(", "));
      toast.success(res.door_milestones.length
        ? `Milestones set — staff screens flash at ${res.door_milestones.join(", ")} guests`
        : "Door milestones cleared");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const saveVenueProfile = async () => {
    try {
      const payload = {
        name: editForm.name.trim(), address: editForm.address.trim(),
        city: editForm.city.trim(), open_until: editForm.open_until.trim(),
        cover: editForm.cover === "" ? 0 : Number(editForm.cover),
      };
      if (editForm.geo?.lat && editForm.geo?.lng) {
        payload.lat = editForm.geo.lat;
        payload.lng = editForm.geo.lng;
      }
      const { data: res } = await API.patch("/venue/profile", payload);
      toast.success(res?.geocoded ? "Venue info updated — map pin moved to the new address" : "Venue info updated");
      setEditForm((f) => ({ ...f, geo: null }));
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const addVenueVideo = async (m) => {
    if (!m) return;
    try {
      await API.post("/venue/videos", { image_url: m.url });
      toast.success("Video added");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const removeVenueVideo = async (url) => {
    try {
      await API.post("/venue/videos/remove", { image_url: url });
      toast.success("Video removed");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const venue = data?.venue;
  const tonight = data?.tonight;
  const report = data?.night_report;
  const subscribed = user?.subscription?.status === "active";

  return (
    <div data-testid="venue-dashboard" className="flex min-h-screen bg-[#050505] text-white"
      style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <aside className="glass flex w-16 flex-col items-center gap-2 border-y-0 border-l-0 py-6 md:w-56 md:items-stretch md:px-4">
        <div className="mb-6 px-2">
          <img src="/logo.png" alt="NITEPASS" className="h-10 w-auto md:h-12" />
          <div className="mt-1 hidden text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 md:block">Venue</div>
        </div>
        {NAV.map((n) => (
          <button key={n.id} data-testid={`venue-nav-${n.id}`} onClick={() => setTab(n.id)}
            className={`flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-bold transition-colors duration-200 ${tab === n.id ? "bg-neon-gold/15 text-neon-gold" : "text-zinc-500 hover:text-white"}`}>
            <n.icon size={20} /> <span className="hidden md:inline">{n.label}</span>
          </button>
        ))}
        <button data-testid="venue-logout" onClick={async () => { await logout(); navigate("/"); }}
          className="mt-auto flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-bold text-zinc-600 hover:text-red-400">
          <SignOut size={20} /> <span className="hidden md:inline">Log out</span>
        </button>
      </aside>

      <main className="flex-1 p-4 md:p-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <button data-testid="venue-back-btn" onClick={() => navigate("/")}
                className="glass rounded-full p-2 text-zinc-400 transition-colors hover:text-neon-gold">
                <ArrowLeft size={16} />
              </button>
              <div className="neon-overline !text-neon-gold">Command Center</div>
            </div>
            {myVenues.length > 1 ? (
              <select data-testid="venue-switcher" value={venue?.id || ""} onChange={(e) => switchVenue(e.target.value)}
                className="mt-1 max-w-[260px] rounded-xl border border-neon-gold/30 bg-black px-3 py-2 font-display text-lg font-black text-neon-gold outline-none">
                {myVenues.map((v) => (
                  <option key={v.id} value={v.id} disabled={!v.active}>
                    {v.name}{v.active ? "" : " (inactive)"}
                  </option>
                ))}
              </select>
            ) : (
              <h1 className="mt-1 font-display text-2xl font-black" data-testid="venue-name">{venue?.name || "Your Venue"}</h1>
            )}
          </div>
          <button data-testid="venue-new-event-btn" onClick={() => setShowEvent(true)} className="btn-neon !py-2 text-sm">+ New Event</button>
        </div>

        {!subscribed && (
          <div className="mt-5 rounded-2xl border border-neon-gold/40 bg-neon-gold/5 p-4" data-testid="subscription-banner">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm"><span className="font-black">Activate NITEPASS VENUE</span> <span className="text-zinc-400">— {formatPlanPrice(pricing[`venue_${billing}`])}/{intervalLabel(pricing[`venue_${billing}`])} unlocks scanner, drops & analytics.</span></div>
              <BillingToggle value={billing} onChange={setBilling} testid="venue-billing-toggle"
                savings={yearlySavings(pricing.venue_monthly, pricing.venue_yearly)} />
            </div>
            <button data-testid="activate-subscription-btn" onClick={() => startCheckout(`venue_${billing}`, { onCityRequired: askCity }).catch((e) => toast.error(fmtErr(e)))}
              className="mt-3 rounded-full bg-neon-gold px-5 py-2 text-xs font-black text-black shadow-glow-gold">Activate — {formatPlanPrice(pricing[`venue_${billing}`])}/{billing === "monthly" ? "mo" : "yr"}</button>
          </div>
        )}

        <FounderCityDialog open={!!cityAsk}
          onSubmit={(c) => { cityAsk?.(c); setCityAsk(null); }}
          onCancel={() => { cityAsk?.(null); setCityAsk(null); }} />

        {tab === "analytics" && (
          <div className="mt-6 space-y-6" data-testid="venue-analytics">
            {!analytics ? (
              <p className="py-10 text-center text-sm text-zinc-600">Loading analytics…</p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
                  {[
                    ["Storefront Views", "venue_view", "text-neon-gold"],
                    ["Event Clicks", "event_click", "text-neon-violet"],
                    ["Map Pin Taps", "map_click", "text-neon-green"],
                    ["Directions Taps", "directions_click", "text-neon-yellow"],
                    ["Rewards Redeemed", "rewards_redeemed", "text-neon-gold"],
                    ["Check-ins", "checkins", "text-neon-green"],
                  ].map(([label, key, accent]) => (
                    <div key={key} className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5" data-testid={`analytics-${key}`}>
                      <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
                      <div className={`mt-2 font-display text-3xl font-black ${accent}`}>{analytics.totals[key] ?? 0}</div>
                      <div className="mt-1 text-[10px] font-bold text-zinc-600">{analytics.today[key] ?? 0} today</div>
                    </div>
                  ))}
                </div>
                <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5">
                  <div className="mb-3 text-[11px] font-bold uppercase tracking-widest text-zinc-500">Last 30 days — storefront views</div>
                  <div className="flex h-32 items-end gap-1" data-testid="analytics-chart">
                    {analytics.daily.map((d) => {
                      const max = Math.max(1, ...analytics.daily.map((x) => x.venue_views));
                      return (
                        <div key={d.date} title={`${d.date}: ${d.venue_views} views · ${d.map_clicks} map taps · ${d.directions_clicks} directions · ${d.rewards_redeemed} rewards`}
                          className="flex-1 rounded-t bg-neon-gold/60 transition-colors hover:bg-neon-gold"
                          style={{ height: `${Math.max(3, (d.venue_views / max) * 100)}%` }} />
                      );
                    })}
                  </div>
                  <div className="mt-2 flex justify-between text-[9px] uppercase tracking-wider text-zinc-600">
                    <span>{analytics.daily[0]?.date?.slice(5)}</span><span>{analytics.daily.at(-1)?.date?.slice(5)}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {tab === "overview" && tonight && (
          <div className="mt-6 space-y-6" data-testid="venue-overview">
            <div>
              <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-neon-green"><span className="h-2 w-2 animate-pulse rounded-full bg-neon-green" /> Tonight — live</div>
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <Stat label="NITEPASS Check-ins" value={tonight.checkins} accent="text-neon-green" />
                <Stat label="Door Revenue" value={`$${tonight.revenue.toLocaleString()}`} accent="text-neon-yellow" />
                <Stat label="Rewards Redeemed" value={tonight.rewards_redeemed} accent="text-neon-gold" />
                <Stat label="Active Drops" value={drops.filter((d) => new Date(d.expires_at) > new Date()).length} accent="text-neon-violet" />
              </div>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="entry-breakdown">
                {Object.entries(tonight.entry_breakdown).map(([k, v]) => (
                  <span key={k} className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-bold">{k}: <span className="text-neon-violet">{v}</span></span>
                ))}
                {Object.keys(tonight.entry_breakdown).length === 0 && <span className="text-xs text-zinc-500">No scans yet tonight. Open the Scanner tab.</span>}
              </div>
            </div>

            {report && (
              <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-6" data-testid="night-report">
                <div className="neon-overline">{report.label}</div>
                <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-3">
                  <Stat label="Total Attendance" value={report.attendance} />
                  <Stat label="NITEPASS Check-ins" value={report.nitepass_checkins} accent="text-neon-violet" />
                  <Stat label="Door Revenue" value={`$${report.door_revenue.toLocaleString()}`} accent="text-neon-yellow" />
                  <Stat label="Rewards Redeemed" value={report.rewards_redeemed} accent="text-neon-gold" />
                  <Stat label="New Customers" value={report.new_customers} accent="text-neon-green" />
                  <Stat label="Returning" value={report.returning_customers} />
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {Object.entries(report.entry_points).map(([k, v]) => (
                    <span key={k} className="rounded-full bg-white/5 px-3 py-1.5 text-xs font-bold text-zinc-300">{k} — {v}</span>
                  ))}
                  <span className="rounded-full bg-neon-purple/15 px-3 py-1.5 text-xs font-black text-neon-purple">Unique: {report.unique_attendance}</span>
                </div>
                <div className="mt-5 overflow-x-auto">
                  <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-zinc-500">Promoter Leaderboard</div>
                  <table className="w-full min-w-[560px] text-sm" data-testid="report-leaderboard">
                    <thead><tr className="border-b border-white/10 text-left text-[11px] uppercase tracking-widest text-zinc-500">
                      <th className="py-2 pr-4">Promoter</th><th className="pr-4">Guests</th><th className="pr-4">Paid</th><th className="pr-4">Revenue</th><th>Payout</th>
                    </tr></thead>
                    <tbody>
                      {report.leaderboard.map((r, i) => (
                        <tr key={r.handle} className="border-b border-white/5">
                          <td className="py-2.5 pr-4 font-bold">{i === 0 && <span className="mr-1 text-neon-yellow">★</span>}{r.handle}</td>
                          <td className="pr-4">{r.guests}</td><td className="pr-4 text-neon-green">{r.paid}</td>
                          <td className="pr-4">${r.revenue.toLocaleString()}</td>
                          <td className="font-bold text-neon-gold">${r.payout.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {venue && (
              <div className="rounded-3xl border border-neon-gold/20 bg-[#0a0a0a] p-6" data-testid="venue-story-card">
                <div className="mb-1 text-xs font-bold uppercase tracking-widest text-neon-gold">Instagram Story Card</div>
                <p className="mb-3 text-[11px] leading-relaxed text-zinc-600">
                  One tap generates a branded TONIGHT AT {venue.name} story (1080×1920) with tonight's event,
                  live drops and your venue photos — ready to post on Instagram.
                </p>
                <div className="flex flex-wrap items-center gap-4">
                  <a data-testid="venue-card-download" href={mediaUrl(`/api/venues/${venue.id}/card.png`)}
                    className="rounded-full bg-neon-gold px-5 py-2.5 text-xs font-black text-black shadow-glow-gold">
                    Download Story Card
                  </a>
                  <a data-testid="venue-recap-download" href={mediaUrl(`/api/venues/${venue.id}/recap.png`)}
                    className="rounded-full border border-neon-violet/40 px-5 py-2.5 text-xs font-black text-neon-violet">
                    Last Night Recap
                  </a>
                  {user?.subscription?.founder && (
                    <a data-testid="venue-founder-card-download" href={mediaUrl(`/api/venues/${venue.id}/founder-card.png`)}
                      className="rounded-full border border-neon-gold/50 bg-neon-gold/10 px-5 py-2.5 text-xs font-black text-neon-gold">
                      Founding Venue Card
                    </a>
                  )}
                  <button data-testid="venue-copy-link-btn"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(`${window.location.origin}/api/share/venue/${venue.id}`);
                        toast.success("Share link copied — unfurls with your TONIGHT card");
                      } catch { toast.error("Copy failed"); }
                    }}
                    className="rounded-full border border-white/15 px-5 py-2.5 text-xs font-black text-zinc-300 transition-colors hover:border-neon-gold/50 hover:text-neon-gold">
                    Copy Share Link
                  </button>
                  {venue.card_background && (
                    <img src={mediaUrl(venue.card_background)} alt="Custom card background"
                      className="h-16 w-10 rounded-lg border border-neon-gold/40 object-cover" data-testid="venue-card-bg-preview" />
                  )}
                  {venue.card_background && (
                    <button data-testid="venue-card-bg-remove" onClick={clearCardBackground}
                      className="text-[11px] font-bold text-zinc-500 transition-colors hover:text-red-400">
                      Remove custom background
                    </button>
                  )}
                </div>
                <div className="mt-3">
                  <MediaUpload imageOnly clearAfterUpload label="Upload custom card background (optional)"
                    onUploaded={setCardBackground} />
                </div>
                <div className="mt-4">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Show on recap card</div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="venue-recap-prefs">
                    {VENUE_RECAP_KEYS.map(([key, label]) => {
                      const prefs = { guests: true, revenue: true, rewards: true, top_promoter: true, ...(venue.recap_prefs || {}) };
                      return (
                        <button key={key} data-testid={`venue-recap-pref-${key}`} onClick={() => toggleVenueRecap(key)}
                          className={`rounded-full border px-3 py-1.5 text-[10px] font-bold transition-colors duration-200 ${prefs[key] ? "border-neon-gold/50 bg-neon-gold/10 text-neon-gold" : "border-white/10 text-zinc-600"}`}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "scanner" && venue && (
          <div className="mt-6 grid gap-6 lg:grid-cols-2" data-testid="scanner-panel">
            <div className="rounded-3xl border border-neon-violet/30 bg-[#060d10] p-6">
              <div className="flex items-center gap-2 font-display text-lg font-black"><QrCode size={22} className="text-neon-violet" /> Door Scanner</div>
              <p className="mt-1 text-xs text-zinc-500">Guest opens NITEPASS → PASS. Paste or type the code from their dynamic QR.</p>
              <input ref={tokenRef} data-testid="scan-token-input" value={scanForm.token}
                onChange={(e) => setScanForm({ ...scanForm, token: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && doScan()}
                placeholder="NTPASS.xxxx.xxxx.xxxx"
                className="mt-4 w-full rounded-xl border border-white/10 bg-black px-4 py-3 font-mono text-xs outline-none focus:border-neon-violet" />
              <button type="button" data-testid="camera-scan-btn" onClick={() => setShowCamera((s) => !s)}
                className="mt-3 w-full rounded-full border border-neon-violet/40 bg-neon-violet/10 py-2.5 text-xs font-black text-neon-violet transition-colors duration-150 hover:bg-neon-violet/20">
                {showCamera ? "Hide Camera" : "Scan with Camera"}
              </button>
              {showCamera && (
                <div className="mt-3">
                  <QrScanner
                    onScan={(decoded) => {
                      setShowCamera(false);
                      setScanForm((f) => ({ ...f, token: decoded }));
                      doScan(decoded);
                    }}
                    onClose={() => setShowCamera(false)}
                  />
                </div>
              )}
              <div className="mt-3 grid grid-cols-2 gap-3">
                <select data-testid="scan-entry-point" value={scanForm.entry_point} onChange={(e) => setScanForm({ ...scanForm, entry_point: e.target.value })}
                  className="rounded-xl border border-white/10 bg-black px-3 py-2.5 text-sm outline-none">
                  {venue.entry_points.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <input data-testid="scan-amount-input" type="number" min="0" placeholder="Amount paid $"
                  value={scanForm.amount_paid} onChange={(e) => setScanForm({ ...scanForm, amount_paid: e.target.value })}
                  className="rounded-xl border border-white/10 bg-black px-3 py-2.5 text-sm outline-none" />
              </div>
              <button data-testid="scan-submit-btn" onClick={() => doScan()} disabled={scanBusy}
                className="mt-4 w-full rounded-full bg-neon-violet py-3 font-display text-sm font-black text-black shadow-glow-violet transition-transform duration-150 hover:-translate-y-0.5 disabled:opacity-50">
                {scanBusy ? "Scanning..." : "Verify & Check In"}
              </button>
              <AnimatePresence>
                {scanResult && (
                  <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                    className="mt-4 rounded-2xl border border-neon-green/40 bg-neon-green/5 p-5 text-center" data-testid="scan-result">
                    {scanResult.already_checked_in ? (
                      <>
                        <div className="font-display text-lg font-black text-neon-yellow">ALREADY CHECKED IN</div>
                        <div className="mt-1 text-sm text-zinc-400">{scanResult.guest_name} · first scan {new Date(scanResult.first_scan).toLocaleTimeString()}</div>
                      </>
                    ) : (
                      <>
                        <div className="neon-overline !text-neon-green">Welcome to {scanResult.venue_name}</div>
                        <div className="mt-1 font-display text-2xl font-black">{scanResult.guest_name}</div>
                        <div className="mt-1 text-xs font-bold text-neon-violet">{scanResult.guest_handle} · {scanResult.entry_type.replace(/_/g, " ")}{scanResult.promoter_handle ? ` · via ${scanResult.promoter_handle}` : ""}</div>
                        <div className="mt-3 font-display text-xl font-black text-neon-yellow text-glow-violet">+{scanResult.points_awarded} NITE POINTS</div>
                        {scanResult.reward_unlocked && (
                          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring" }}
                            className="mt-2 inline-block rounded-full bg-neon-gold px-4 py-1.5 font-display text-sm font-black text-black shadow-glow-gold" data-testid="scan-reward">
                            {scanResult.reward_unlocked} UNLOCKED
                          </motion.div>
                        )}
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-6">
              <div className="mb-3 font-display text-sm font-black">Recent scans</div>
              <div className="max-h-[420px] space-y-2 overflow-y-auto" data-testid="recent-scans">
                {scans.map((s) => (
                  <div key={s.id} className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2.5 text-xs">
                    <div>
                      <span className="font-bold">{s.guest?.name || "Guest"}</span>
                      <span className="ml-2 text-zinc-500">{s.entry_point}</span>
                    </div>
                    <div className="text-right text-zinc-500">
                      {s.amount_paid > 0 && <span className="mr-2 font-bold text-neon-green">${s.amount_paid}</span>}
                      {new Date(s.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "events" && (
          <div className="mt-6 overflow-x-auto" data-testid="venue-events-table">
            <table className="w-full min-w-[680px] text-sm">
              <thead><tr className="border-b border-white/10 text-left text-[11px] uppercase tracking-widest text-zinc-500">
                <th className="py-3 pr-4">Event</th><th className="pr-4">Promoter</th><th className="pr-4">Going</th><th className="pr-4">List</th><th className="pr-4">Checked In</th><th>Cover</th>
              </tr></thead>
              <VenueEvents venueId={data?.venue?.id} />
            </table>
          </div>
        )}

        {tab === "drops" && (
          <div className="mt-6 grid gap-6 lg:grid-cols-2" data-testid="drops-panel">
            <form onSubmit={createDrop} className="rounded-3xl border border-neon-green/30 bg-[#07120b] p-6">
              <div className="flex items-center gap-2 font-display text-lg font-black"><Lightning size={20} weight="fill" className="text-neon-green" /> Create NITE DROP</div>
              <p className="mt-1 text-xs text-zinc-500">Slow night? Drop a limited reward. Members who favorited you get notified instantly.</p>
              <input data-testid="drop-perk-input" required placeholder="Perk (e.g. FREE ENTRY + FREE DRINK)" value={dropForm.perk}
                onChange={(e) => setDropForm({ ...dropForm, perk: e.target.value })}
                className="mt-4 w-full rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none focus:border-neon-green" />
              <div className="mt-3 grid grid-cols-2 gap-3">
                <input type="number" min="1" value={dropForm.total} onChange={(e) => setDropForm({ ...dropForm, total: e.target.value })}
                  placeholder="First N members" className="rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none" />
                <input type="number" min="5" value={dropForm.minutes} onChange={(e) => setDropForm({ ...dropForm, minutes: e.target.value })}
                  placeholder="Expires in (min)" className="rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none" />
              </div>
              <button data-testid="drop-submit-btn" className="mt-4 w-full rounded-full bg-neon-green py-3 font-display text-sm font-black text-black shadow-glow-green">Launch Drop</button>
            </form>
            <div className="space-y-3">
              {drops.map((d) => {
                const live = new Date(d.expires_at) > new Date();
                return (
                  <div key={d.id} className={`rounded-2xl border p-4 ${live ? "border-neon-green/40 bg-neon-green/5" : "border-white/10 bg-white/[0.02] opacity-60"}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase tracking-widest text-neon-green">{live ? "LIVE" : "EXPIRED"}</span>
                      <span className="text-xs text-zinc-500">{d.claimed}/{d.total} claimed</span>
                    </div>
                    <div className="mt-1 font-display text-sm font-bold">{d.perk}</div>
                  </div>
                );
              })}
              {drops.length === 0 && <p className="py-8 text-center text-sm text-zinc-500">No drops yet.</p>}
            </div>
          </div>
        )}

        {tab === "promoters" && (
          <div className="mt-6 max-w-3xl" data-testid="promoters-panel">
            <div className="flex gap-2">
              <input data-testid="invite-email-input" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} type="text"
                placeholder="Find promoter by email, name, or @handle"
                className="flex-1 rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none focus:border-neon-gold" />
              <button data-testid="invite-promoter-btn" onClick={() => invite()} className="rounded-full bg-neon-gold px-5 py-2 text-xs font-black text-black shadow-glow-gold">Add to Team</button>
            </div>
            {promoterResults.length > 0 && (
              <div className="mt-3 space-y-2" data-testid="promoter-search-results">
                {promoterResults.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm">
                    <div>
                      <span className="font-bold">{p.name}</span>
                      <span className="ml-2 text-xs text-neon-violet">{p.handle}</span>
                      <div className="text-[11px] text-zinc-500">{p.title || "Party Promoter"} · {p.email}{p.score ? ` · Score ${p.score}` : ""}</div>
                    </div>
                    <button data-testid={`add-promoter-${p.id}`} onClick={() => invite(p.handle)}
                      className="rounded-full border border-neon-gold/40 px-3 py-1.5 text-[11px] font-black text-neon-gold">Add</button>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-5 overflow-x-auto">
              <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-zinc-500">Performance (all events)</div>
              <table className="w-full min-w-[600px] text-sm" data-testid="promoter-leaderboard">
                <thead><tr className="border-b border-white/10 text-left text-[11px] uppercase tracking-widest text-zinc-500">
                  <th className="py-2 pr-4">Promoter</th><th className="pr-4">Score</th><th className="pr-4">Traffic</th><th className="pr-4">Paid</th><th className="pr-4">Revenue</th><th>Payout Owed</th>
                </tr></thead>
                <tbody>
                  {data?.leaderboard.map((r) => (
                    <tr key={r.handle} className="border-b border-white/5">
                      <td className="py-2.5 pr-4 font-bold">{r.handle}<span className="ml-2 text-[10px] font-normal text-neon-gold">{r.title || "Party Promoter"}</span></td>
                      <td className="pr-4 text-neon-yellow">{r.score ?? "—"}</td>
                      <td className="pr-4">{r.checked_in}</td>
                      <td className="pr-4 text-neon-green">{r.paid}</td>
                      <td className="pr-4">${r.revenue.toLocaleString()}</td>
                      <td className="font-bold text-neon-gold">${r.owed.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="promoter-team-list">
              {venue?.promoter_team.map((m) => (
                <span key={m.user_id} className="rounded-full border border-neon-violet/30 bg-neon-violet/5 px-3 py-1.5 text-xs font-bold text-neon-violet">
                  {m.handle || m.name || m.user_id} · {m.title || "Party Promoter"} · {m.status}
                </span>
              ))}
            </div>
          </div>
        )}

        {tab === "edit" && venue && (
          <div className="mt-6 max-w-2xl space-y-4" data-testid="edit-venue-panel">
            <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-6">
              <div className="font-display text-lg font-black">Venue Info</div>
              <p className="mt-1 text-xs text-zinc-500">Shown on your public venue page and the /venues directory.</p>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Venue name
                  <input data-testid="edit-venue-name" value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="mt-1 w-full rounded-xl border border-white/10 bg-black px-3 py-2.5 text-sm normal-case outline-none focus:border-neon-gold" />
                </label>
                <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">City
                  <input data-testid="edit-venue-city" value={editForm.city}
                    onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} placeholder="San Antonio"
                    className="mt-1 w-full rounded-xl border border-white/10 bg-black px-3 py-2.5 text-sm normal-case outline-none focus:border-neon-gold" />
                </label>
                <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 md:col-span-2">Address
                  <AddressAutocomplete data-testid="edit-venue-address" value={editForm.address}
                    onChange={(e) => setEditForm({ ...editForm, address: e.target.value, geo: null })}
                    onPlaceSelect={(place) => setEditForm((f) => ({
                      ...f, address: place.address, city: place.city || f.city,
                      geo: place.lat && place.lng ? { lat: place.lat, lng: place.lng } : null,
                    }))}
                    placeholder="Start typing — pick your venue from Google"
                    className="mt-1 w-full rounded-xl border border-white/10 bg-black px-3 py-2.5 text-sm normal-case outline-none focus:border-neon-gold" />
                  {editForm.geo && (
                    <span className="mt-1 block text-[10px] font-bold normal-case tracking-normal text-neon-green" data-testid="edit-venue-verified">
                      Verified address — pin will move to this exact spot
                    </span>
                  )}
                </label>
                <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Open until
                  <input data-testid="edit-venue-open-until" value={editForm.open_until}
                    onChange={(e) => setEditForm({ ...editForm, open_until: e.target.value })} placeholder="2 AM"
                    className="mt-1 w-full rounded-xl border border-white/10 bg-black px-3 py-2.5 text-sm normal-case outline-none focus:border-neon-gold" />
                </label>
                <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Cover ($)
                  <input data-testid="edit-venue-cover" type="number" min="0" value={editForm.cover}
                    onChange={(e) => setEditForm({ ...editForm, cover: e.target.value })}
                    className="mt-1 w-full rounded-xl border border-white/10 bg-black px-3 py-2.5 text-sm outline-none focus:border-neon-gold" />
                </label>
              </div>
              <div className="mt-4 flex justify-end">
                <button data-testid="edit-venue-save" onClick={saveVenueProfile}
                  className="rounded-full bg-neon-gold px-5 py-2 text-xs font-black text-black shadow-glow-gold">Save Info</button>
              </div>
            </div>

            <div className="rounded-3xl border border-neon-violet/25 bg-[#0a0612] p-6" data-testid="add-venue-card">
              <div className="font-display text-lg font-black">Add Another Venue</div>
              <p className="mt-1 text-xs leading-relaxed text-zinc-500">
                Run multiple spots under one account — {formatPlanPrice(pricing.venue_extra_monthly)}/month or{" "}
                {formatPlanPrice(pricing.venue_extra_yearly)}/year per additional venue. Each gets its own scanner,
                drops, analytics and story cards.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <BillingToggle value={addBilling} onChange={setAddBilling} testid="add-venue-billing"
                  savings={yearlySavings(pricing.venue_extra_monthly, pricing.venue_extra_yearly)} />
                <button data-testid="add-venue-btn"
                  onClick={() => startCheckout(`venue_extra_${addBilling}`).catch((e) => toast.error(fmtErr(e)))}
                  className="rounded-full bg-neon-violet px-5 py-2 text-xs font-black text-black">
                  Add Venue — {formatPlanPrice(pricing[`venue_extra_${addBilling}`])}/{addBilling === "monthly" ? "mo" : "yr"}
                </button>
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-6" data-testid="venue-photos">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-zinc-500">
                Venue Photos ({(venue.images || []).length}/8)
              </div>
              <p className="mb-3 text-[11px] text-zinc-600">Guests see these when they open your venue page.</p>
              {(venue.images || []).length > 0 && (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="venue-photos-grid">
                  {venue.images.map((img, i) => (
                    <div key={img} className="relative overflow-hidden rounded-xl border border-white/10">
                      <img src={mediaUrl(img)} alt="" className="h-28 w-full object-cover" data-testid={`venue-photo-${i}`} />
                      <button type="button" data-testid={`venue-photo-remove-${i}`} onClick={() => removeVenueImage(img)}
                        className="glass absolute right-1.5 top-1.5 rounded-full p-1 text-zinc-300 hover:text-red-400">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3">
                <MediaUpload imageOnly clearAfterUpload label="Add venue photo" onUploaded={addVenueImage} />
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-6" data-testid="venue-videos">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-zinc-500">
                Venue Videos ({(venue.videos || []).length}/4)
              </div>
              <p className="mb-3 text-[11px] text-zinc-600">Short clips sell the vibe — mp4 or webm, shown on your venue page.</p>
              {(venue.videos || []).length > 0 && (
                <div className="grid grid-cols-2 gap-3" data-testid="venue-videos-grid">
                  {venue.videos.map((vid, i) => (
                    <div key={vid} className="relative overflow-hidden rounded-xl border border-white/10">
                      <video src={mediaUrl(vid)} controls preload="metadata" className="h-32 w-full object-cover" data-testid={`venue-video-${i}`} />
                      <button type="button" data-testid={`venue-video-remove-${i}`} onClick={() => removeVenueVideo(vid)}
                        className="glass absolute right-1.5 top-1.5 rounded-full p-1 text-zinc-300 hover:text-red-400">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3">
                <MediaUpload videoOnly clearAfterUpload label="Add venue video (mp4 / webm)" onUploaded={addVenueVideo} />
              </div>
            </div>
          </div>
        )}

        {tab === "staff" && venue && (
          <div className="mt-6 max-w-2xl space-y-4" data-testid="staff-panel">
            <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-6">
              <div className="font-display text-lg font-black">Door Staff</div>
              <p className="mt-1 text-xs leading-relaxed text-zinc-500">
                Staff get scanner-only access attached to {venue.name} — no analytics, no wallet.
                They'll see a Door Scanner button in their NITEPASS profile.
              </p>
              <div className="mt-4 flex gap-2">
                <input data-testid="staff-email-input" value={staffEmail} onChange={(e) => setStaffEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addStaff()}
                  placeholder="Staff member's email"
                  className="flex-1 rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none focus:border-neon-gold" />
                <button data-testid="staff-add-btn" onClick={addStaff} disabled={!staffEmail.trim()}
                  className="rounded-full bg-neon-gold px-5 py-2.5 text-xs font-black text-black shadow-glow-gold disabled:opacity-40">
                  Add Staff
                </button>
              </div>
              <div className="mt-4 space-y-2" data-testid="staff-list">
                {staffList.map((s) => (
                  <div key={s.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                    <div>
                      <div className="text-sm font-bold">{s.name} <span className="text-xs font-bold text-neon-violet">{s.handle}</span></div>
                      <div className="text-[11px] text-zinc-500">{s.email} · {s.scans} scans</div>
                    </div>
                    <button data-testid={`staff-remove-${s.id}`} onClick={() => removeStaff(s.id)}
                      className="text-[11px] font-bold text-zinc-500 transition-colors hover:text-red-400">Remove</button>
                  </div>
                ))}
                {staffList.length === 0 && (
                  <p className="py-3 text-sm text-zinc-500">No door staff yet. Add your door team by email.</p>
                )}
              </div>
            </div>

            <div className="rounded-3xl border border-neon-gold/20 bg-[#0a0a0a] p-6" data-testid="door-milestones-panel">
              <div className="font-display text-lg font-black">Door Milestones</div>
              <p className="mt-1 text-xs leading-relaxed text-zinc-500">
                When tonight's door count crosses one of these numbers, every staff scanner screen flashes
                a celebration (e.g. "200 GUESTS!"). Comma-separated, up to 10.
              </p>
              <div className="mt-4 flex gap-2">
                <input data-testid="door-milestones-input" value={milestonesInput}
                  onChange={(e) => setMilestonesInput(e.target.value)}
                  placeholder="50, 100, 200"
                  className="flex-1 rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none focus:border-neon-gold" />
                <button data-testid="door-milestones-save" onClick={saveDoorMilestones}
                  className="rounded-full bg-neon-gold px-5 py-2.5 text-xs font-black text-black shadow-glow-gold">
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === "messages" && <div className="mt-6"><MessagesPanel userRole="venue" /></div>}

        {tab === "wallet" && <div className="mt-6"><WalletPanel /></div>}

        {tab === "rewards" && venue && (
          <div className="mt-6 max-w-2xl" data-testid="rewards-panel">
            <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-zinc-500">Your reward ladder — verified by scan</div>
            <div className="space-y-2">
              {venue.rewards_program.map((m) => (
                <div key={m.visits} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                  <span className="text-xs font-black text-neon-violet">{m.visits} {m.visits === 1 ? "VISIT" : "VISITS"}</span>
                  <span className="text-sm font-bold">{m.reward}</span>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-zinc-500">Redeemed tonight: <span className="font-bold text-neon-gold">{tonight?.rewards_redeemed || 0}</span>. Fraud-proof: a QR scan at an authorized entry point verifies every visit.</p>

            <div className="mt-8" data-testid="np-reward-acceptance">
              <div className="mb-1 text-[11px] font-bold uppercase tracking-widest text-zinc-500">NITEPASS rewards you accept at the door</div>
              <p className="mb-3 text-[11px] text-zinc-600">Guests redeem these platform rewards with NITE POINTS. Toggle off any reward you don't honor.</p>
              {npRewards === null ? (
                <p className="text-xs text-zinc-600">Loading…</p>
              ) : npRewards.length === 0 ? (
                <p className="text-xs text-zinc-600">No NITEPASS rewards are live right now.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {npRewards.map((r) => (
                    <button key={r.id} data-testid={`reward-accept-${r.id}`} onClick={() => toggleNpReward(r)}
                      className={`rounded-full border px-3.5 py-1.5 text-[11px] font-bold transition-colors duration-200 ${r.accepted ? "border-neon-gold/50 bg-neon-gold/10 text-neon-gold" : "border-white/10 text-zinc-600 line-through"}`}>
                      {r.title} · {r.point_cost} pts{r.venue_specific ? " · this venue only" : ""}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {showEvent && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" data-testid="venue-event-modal">
          <form onSubmit={createEvent} className="glass max-h-[90vh] w-full max-w-md space-y-3 overflow-y-auto rounded-3xl p-6">
            <h3 className="font-display text-lg font-black">New Event at {venue?.name}</h3>
            <input required placeholder="Event title" value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
              className="w-full rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none focus:border-neon-gold" data-testid="venue-event-title" />
            <div className="grid grid-cols-3 gap-3">
              <input value={eventForm.date_label} onChange={(e) => setEventForm({ ...eventForm, date_label: e.target.value })} placeholder="Date"
                className="rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none" />
              <input value={eventForm.start} onChange={(e) => setEventForm({ ...eventForm, start: e.target.value })} placeholder="Opens"
                className="rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none" />
              <input value={eventForm.end} onChange={(e) => setEventForm({ ...eventForm, end: e.target.value })} placeholder="Closes"
                className="rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="number" min="0" value={eventForm.cover} onChange={(e) => setEventForm({ ...eventForm, cover: e.target.value })} placeholder="Cover $"
                className="rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none" />
              <input value={eventForm.list_perk} onChange={(e) => setEventForm({ ...eventForm, list_perk: e.target.value })} placeholder="List perk"
                className="rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none" />
            </div>
            <input value={eventForm.address} onChange={(e) => setEventForm({ ...eventForm, address: e.target.value })} placeholder={`Address (defaults to ${venue?.address || "venue address"})`}
              className="w-full rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none" data-testid="venue-event-address" />
            <input value={eventForm.specials} onChange={(e) => setEventForm({ ...eventForm, specials: e.target.value })} placeholder="Special drinks / offers (optional)"
              className="w-full rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none" data-testid="venue-event-specials" />
            <input value={eventForm.promoter_email} onChange={(e) => setEventForm({ ...eventForm, promoter_email: e.target.value })} placeholder="Assign promoter email (optional)" type="email"
              className="w-full rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none" />
            <MediaUpload onUploaded={(m) => setEventForm((f) => ({
              ...f,
              image_path: m && m.content_type.startsWith("image/") ? m.path : null,
              media: m && m.content_type.startsWith("video/") ? [m] : [],
            }))} />
            <label className="flex items-center gap-2 text-xs text-zinc-400">
              <input type="checkbox" checked={eventForm.tonight} onChange={(e) => setEventForm({ ...eventForm, tonight: e.target.checked })} /> Happening tonight
            </label>
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => setShowEvent(false)} className="btn-ghost-neon flex-1 !py-2.5 text-sm">Cancel</button>
              <button data-testid="venue-event-submit" type="submit" className="btn-neon flex-1 !py-2.5 text-sm">Publish</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function VenueEvents({ venueId }) {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    if (venueId) API.get("/events").then((r) => setEvents(r.data.filter((e) => e.venue_id === venueId))).catch(() => {});
  }, [venueId]);
  return (
    <tbody>
      {events.map((e) => (
        <tr key={e.id} className="border-b border-white/5">
          <td className="py-3 pr-4 font-bold">{e.title}
            {e.status === "pending_payment" && (
              <span className="ml-2 rounded-full bg-neon-yellow/15 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-neon-yellow" data-testid={`event-draft-${e.id}`}>Draft · pay to publish</span>
            )}
            <div className="text-[11px] font-normal text-zinc-500">{e.date_label} · {e.start}{e.end ? `–${e.end}` : ""}</div>{e.specials && <div className="text-[10px] font-normal text-neon-gold">{e.specials}</div>}</td>
          <td className="pr-4 text-zinc-400">{e.promoter_handle || "House"}</td>
          <td className="pr-4">{e.going_count}</td>
          <td className="pr-4">{e.list_count}</td>
          <td className="pr-4 text-neon-green">{e.checked_in}</td>
          <td>${e.cover}</td>
        </tr>
      ))}
    </tbody>
  );
}
